import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment';
import FormatListNumberedOutlinedIcon from '@material-ui/icons/FormatListNumbered';
import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined';
import {
    retrieveTasksSummaryData,
    TaskSummary,
    TaskStatusEnum,
    TasksSummaryQueryParams,
} from '../../../tasksSummary/TasksSummaryRepository';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import { DueListRow, DueListWidget } from './DueListWidget';
import { formatDueLabel } from './formatDueLabel';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT } from '../QuickViewDrawer';
import { runWithMinimumVisibleRefresh } from './RefreshingIndicator';

const DUE_LIST_ROW_LIMIT = 5;

// Adhoc tasks due today or earlier, assigned to the signed-in person - same params
// (isPersonalTask/isMyTasksOnly/isFromDataSource/isIncludeFollowUpTasksOnly/due) the mobile
// app's own TasksDueCardStack.tsx uses against this identical task.summaryList field, just
// scoped down to a handful of rows for a glance card instead of a full swipeable stack.
export interface TasksDueWidgetProps {
    /** Opens the task inline (TasksSummaryFormDialog/PhoneFormDialog/EnquiryFormDrawer, routed and mounted once in QuickViewDrawer.tsx) rather than navigating away - explicit request. */
    onOpenTask: (task: TaskSummary) => void;
}

// Static half of the query params, factored out of the effect below so the infinite-scroll
// load-more call can build the identical filter with just a different offset/first, without
// repeating this whole object a second time.
function buildParams(offset: number, first: number): TasksSummaryQueryParams {
    return {
        offset,
        first,
        filter: null,
        sortColumn: 'DueDate',
        sortDirection: 'ASC',
        taskStatus: TaskStatusEnum.Open,
        isIncludeCrmTasks: false,
        isMyTasksOnly: false,
        isPersonalTask: true,
        isFromDataSource: true,
        groupBy: null,
        groupByValue: null,
        completedFromDate: null,
        completedToDate: null,
        updatedFromDate: null,
        updatedToDate: null,
        priorityList: null,
        statusList: null,
        allocatedToList: null,
        allocatedByList: null,
        created: null,
        due: 'todayAndEarlier',
        teamList: null,
        taskTypes: null,
        clientList: null,
        matterList: null,
        isIncludeFollowUpTasksOnly: null,
        isIncludeHasTasks: null,
    };
}

export const TasksDueWidget: React.FC<TasksDueWidgetProps> = (props) => {
    const [tasks, setTasks] = useState<TaskSummary[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // Drives DueListWidget's own RefreshingIndicator - see YourEnquiriesWidget.tsx's identical
    // comment for why this is a separate flag from isLoading (which only the initial mount
    // touches).
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Factored out of the mount effect below so Quick View's own header Refresh button
    // (QuickViewDrawer.tsx) can call the exact same first-page fetch again, resetting the
    // list back to page 1 rather than re-requesting however many rows infinite-scroll had
    // loaded (onLoadMore's own accumulated offset is separate from this).
    const fetchFirstPage = (onSuccess: (tasks: TaskSummary[], totalCount: number) => void, onSettled?: () => void) => {
        retrieveTasksSummaryData(
            buildParams(0, DUE_LIST_ROW_LIMIT),
            true,
            (data) => {
                onSuccess(data.task.summaryList.tasks, data.task.summaryList.actualRowCountForSearch);
                onSettled?.();
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                showNotification('Failed to Fetch Tasks Due', reason, 'error');
                onSettled?.();
            },
            // batchGroup opts this widget's query out of the default shared batch bucket -
            // see YourEnquiriesWidget.tsx's identical comment.
            { batchGroup: 'quickView-tasksDue' },
        );
    };

    useEffect(() => {
        fetchFirstPage((tasksData, totalCountData) => {
            setTasks(tasksData);
            setTotalCount(totalCountData);
            setIsLoading(false);
        });
    // tslint:disable-next-line: align
    }, []);

    // See YourEnquiriesWidget.tsx's own identical comment for why fetchFirstPageRef/the
    // empty-deps effect/the explicit cleanup are each needed. Deliberately doesn't touch
    // isLoading (only the initial mount does), so a manual refresh swaps the rows in place
    // instead of flashing the whole card back to its skeleton.
    const fetchFirstPageRef = React.useRef(fetchFirstPage);
    fetchFirstPageRef.current = fetchFirstPage;
    useEffect(() => {
        // Explicit request: also refresh on the Task page's own task subscription (scoped
        // 'task' - see TasksSummaryPage.tsx's subscribeToTask), not just Quick View's header
        // Refresh button (which emits with no scope, i.e. "refresh everything" - only bail
        // out when a scope is given AND it isn't ours).
        const onRefresh = (scope?: string) => {
            if (scope && scope !== 'task') {
                return;
            }
            runWithMinimumVisibleRefresh(setIsRefreshing, () => new Promise<void>((resolve) => {
                fetchFirstPageRef.current(
                    (tasksData, totalCountData) => {
                        setTasks(tasksData);
                        setTotalCount(totalCountData);
                    },
                    () => resolve()
                );
            }));
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    const onOpenTasksSummary = () => {
        browserHistory.push(linkToPage('tasksSummary'));
    };

    const hasMore = (tasks?.length ?? 0) < totalCount;

    // Infinite-scroll load-more (explicit request) - fetches the next page at the current
    // row count's offset and appends it onto what's already loaded.
    const onLoadMore = () => {
        if (isLoadingMore || !hasMore || !tasks) {
            return;
        }
        setIsLoadingMore(true);
        retrieveTasksSummaryData(
            buildParams(tasks.length, DUE_LIST_ROW_LIMIT),
            true,
            (data) => {
                setTasks((previousTasks) => [...(previousTasks ?? []), ...data.task.summaryList.tasks]);
                setIsLoadingMore(false);
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                setIsLoadingMore(false);
                showNotification('Failed to Fetch Tasks Due', reason, 'error');
            },
            { batchGroup: 'quickView-tasksDue' },
        );
    };

    const rows: DueListRow[] | null = tasks?.map((task) => {
        const due = formatDueLabel(task.dueDate);
        const primary = task.taskDescription ?? 'Task';
        // No single "person" field on a task the way an enquiry has firstName/lastName - the
        // card's bold text here is the task description itself, so initials come from its
        // first couple of words instead (e.g. "Follow up call" -> "FU").
        const initials = primary
            .trim()
            .split(/\s+/)
            .map(word => word[0])
            .filter(Boolean)
            .slice(0, 2)
            .join('')
            .toUpperCase() || '?';
        return {
            key: task.taskGuidID,
            primary,
            // matter/client (not taskTypeDescription) - same as this row's own pre-card
            // secondary already was, just renamed for the card's natureOfEnquiry-equivalent
            // subtitle line.
            secondary: task.matter ?? task.client ?? undefined,
            dueLabel: due.label,
            isOverdue: due.isOverdue,
            onClick: () => props.onOpenTask(task),
            initials,
            statusLabel: task.taskStatusDescription,
            categoryLabel: task.taskTypeDescription,
            updatedLabel: task.updatedDate ? moment(task.updatedDate).format('D MMM YYYY') : null,
        };
    }) ?? null;

    return (
        <DueListWidget
            icon={<FormatListNumberedOutlinedIcon fontSize="small" />}
            iconBg="#e1f5ee"
            iconColor="#0f6e56"
            title="Tasks Due"
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            rows={rows}
            totalCount={totalCount}
            emptyMessage="Nothing due on your tasks right now."
            onViewAll={onOpenTasksSummary}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            variant="card"
            categoryIcon={<LabelOutlinedIcon />}
        />
    );
};
